import React, { useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowLeft, Check, ExternalLink, ShieldCheck, Wrench } from "lucide-react";
import { getPage } from "../../data/productPages";
import { Header } from "../Header";
import { Footer } from "../Footer";

/**
 * Phase 2L-A29 — Shared layout for /products/{slug} and /services/{slug}.
 *
 * Internal route navigation only. Returning to an estimate uses
 * window.history.back() so the customer lands EXACTLY where they were,
 * with all their selections preserved (no re-fetch, no re-render lock).
 */
export default function ProductServicePage({ slugPath }) {
  const navigate = useNavigate();
  const location = useLocation();
  const page = getPage(slugPath);

  useEffect(() => { window.scrollTo(0, 0); }, [slugPath]);

  // Phase 2L-A29 (iter45 fix): document.referrer is unreliable across SPA
  // <Link> navigation (pushState does NOT update it), so we rely PRIMARILY
  // on react-router location.state and fall back to document.referrer only
  // for hard-loaded deep-links (e.g. user pastes /products/tr20uv into a
  // new tab from an estimate they had open).
  const stateFromEstimate = !!location.state?.fromEstimate;
  const referrerFromEstimate =
    typeof document !== "undefined" &&
    /\/proposals\//.test(document.referrer || "");
  const cameFromEstimate = stateFromEstimate || referrerFromEstimate;
  const returnTo = location.state?.returnTo || null;

  if (!page) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="max-w-3xl mx-auto px-6 pt-28 lg:pt-36 pb-24 text-center">
          <h1 className="font-display text-3xl font-bold text-slate-900 mb-3">Page not found</h1>
          <p className="text-slate-600 mb-6">This product or service page doesn't exist yet.</p>
          <Link
            data-testid="product-page-not-found-home"
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-cyan-600 text-white font-semibold hover:bg-cyan-700"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const backToEstimate = () => {
    // Prefer history.back() / navigate(-1) so the customer lands on the
    // ORIGINAL estimate history entry (preserves React state + scroll +
    // any selection toggles made before clicking View details).
    // If we have no usable history (e.g. opened in a fresh tab), fall back
    // to a hard navigate(returnTo) — still correct destination, but new
    // history entry.
    if (window.history.length > 1 && (stateFromEstimate || referrerFromEstimate)) {
      navigate(-1);
    } else if (returnTo) {
      navigate(returnTo);
    } else {
      navigate("/");
    }
  };

  const kindBadge =
    page.kind === "product"
      ? { label: "Product", icon: <ShieldCheck className="w-3.5 h-3.5" /> }
      : { label: "Service", icon: <Wrench className="w-3.5 h-3.5" /> };

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`product-page-${slugPath.replace(/\//g, "-")}`}>
      <Header />

      {/* Phase 2L-A35 — top spacing fix.
          The site-wide `<Header />` is `fixed top-0` (see Header.jsx) so
          any first-after-header content needs to clear ~76–88px of header
          height. About.jsx / Contact.jsx use `pt-28 lg:pt-36`; we follow
          the same pattern. When the "Back to estimate" strip is visible
          it absorbs the offset; when it's not, the offset moves to <main>
          so the hero title never sits under the menu. */}
      {cameFromEstimate && (
        <div className="bg-cyan-50 border-b border-cyan-100 mt-20 lg:mt-24">
          <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
            <span className="text-sm text-cyan-900">
              You're viewing this page from your estimate.
            </span>
            <button
              data-testid="back-to-estimate-top"
              onClick={backToEstimate}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-700 hover:text-cyan-900"
            >
              <ArrowLeft className="w-4 h-4" /> Back to your estimate
            </button>
          </div>
        </div>
      )}

      <main className={`max-w-5xl mx-auto px-6 pb-16 sm:pb-20 ${cameFromEstimate ? "pt-10 sm:pt-12" : "pt-28 lg:pt-36"}`}>
        {/* ───────────────────────── HERO ───────────────────────── */}
        <section className="grid md:grid-cols-2 gap-8 items-start mb-12">
          <div>
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase font-bold tracking-wider px-2.5 py-1 rounded-full bg-slate-900 text-white mb-4">
              {kindBadge.icon} {kindBadge.label}
            </span>
            <h1
              data-testid="product-page-name"
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight"
            >
              {page.name}
            </h1>
            <p
              data-testid="product-page-tagline"
              className="text-base sm:text-lg text-slate-600 mt-4 leading-relaxed"
            >
              {page.tagline}
            </p>

            {/* Hero CTA — same destination as the top strip but more prominent. */}
            <div className="mt-6 flex flex-wrap gap-3">
              {cameFromEstimate ? (
                <button
                  data-testid="back-to-estimate-hero"
                  onClick={backToEstimate}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-cyan-600 text-white font-semibold hover:bg-cyan-700 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to your estimate
                </button>
              ) : (
                <Link
                  data-testid="hero-cta-quote"
                  to="/contact"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-cyan-600 text-white font-semibold hover:bg-cyan-700 transition-colors"
                >
                  Request a quote
                </Link>
              )}
              <Link
                to="/knowledge-hub"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-300 text-slate-700 font-semibold hover:bg-slate-100 transition-colors"
              >
                Knowledge hub
              </Link>
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden bg-white border border-slate-200 aspect-[4/3] flex items-center justify-center">
            {page.hero_image ? (
              <img
                src={page.hero_image}
                alt={page.name}
                className="w-full h-full object-cover"
                loading="lazy"
                referrerPolicy="no-referrer"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                data-testid="product-page-hero-image"
              />
            ) : null}
          </div>
        </section>

        {/* ───────────────────────── BODY ───────────────────────── */}
        <div className="grid lg:grid-cols-[1fr_280px] gap-10">
          <article className="space-y-10">
            {page.what_it_does?.length > 0 && (
              <Block testid="block-what" title="What it does">
                <ul className="space-y-2.5">
                  {page.what_it_does.map((line, i) => (
                    <li key={i} className="flex gap-3 text-slate-700 leading-relaxed">
                      <span className="text-cyan-600 mt-1.5 flex-shrink-0">▸</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </Block>
            )}

            {page.best_for?.length > 0 && (
              <Block testid="block-best-for" title="Best for">
                <BulletList items={page.best_for} />
              </Block>
            )}

            {page.included?.length > 0 && (
              <Block testid="block-included" title="What's included">
                <BulletList items={page.included} icon={<Check className="w-4 h-4 text-emerald-600" />} />
              </Block>
            )}

            {page.benefits?.length > 0 && (
              <Block testid="block-benefits" title="Key benefits">
                <BulletList items={page.benefits} icon={<Check className="w-4 h-4 text-cyan-600" />} />
              </Block>
            )}

            {page.maintenance?.length > 0 && (
              <Block testid="block-maintenance" title="Ongoing maintenance">
                <BulletList items={page.maintenance} />
              </Block>
            )}

            {page.disclaimer && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 leading-relaxed" data-testid="block-disclaimer">
                <strong className="font-semibold">Important: </strong>
                {page.disclaimer}
              </div>
            )}
          </article>

          {/* ───── Sidebar — Related + footer CTA ───── */}
          <aside className="space-y-6">
            {page.related?.length > 0 && (
              <div className="rounded-2xl bg-white border border-slate-200 p-5">
                <h3 className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-3">Related</h3>
                <ul className="space-y-2">
                  {page.related.map((r, i) => (
                    <li key={i}>
                      <Link
                        data-testid={`related-link-${i}`}
                        to={r.to}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-700 hover:text-cyan-900 hover:underline"
                      >
                        {r.label} <ExternalLink className="w-3 h-3" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rounded-2xl bg-slate-900 text-white p-5">
              <h3 className="font-display font-bold text-lg mb-2">
                {cameFromEstimate ? "Ready to decide?" : "Want this for your home?"}
              </h3>
              <p className="text-sm text-slate-300 mb-4 leading-relaxed">
                {cameFromEstimate
                  ? "Return to your estimate to accept or adjust the options."
                  : "Get a quote from a local PWS technician with no obligation."}
              </p>
              {cameFromEstimate ? (
                <button
                  data-testid="back-to-estimate-sidebar"
                  onClick={backToEstimate}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to estimate
                </button>
              ) : (
                <Link
                  to="/contact"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold transition-colors"
                >
                  Request a quote
                </Link>
              )}
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function Block({ title, children, testid }) {
  return (
    <section data-testid={testid}>
      <h2 className="font-display text-2xl font-bold text-slate-900 mb-4">{title}</h2>
      {children}
    </section>
  );
}

function BulletList({ items, icon }) {
  return (
    <ul className="space-y-2.5">
      {items.map((line, i) => (
        <li key={i} className="flex gap-3 text-slate-700 leading-relaxed">
          <span className="flex-shrink-0 mt-1">
            {icon || <span className="block w-1.5 h-1.5 rounded-full bg-slate-400 mt-2" />}
          </span>
          <span>{line}</span>
        </li>
      ))}
    </ul>
  );
}
